import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiArrowLeft, FiArrowRight, FiCoffee } = FiIcons;

const TrustedBy = () => {
  const { t } = useTranslation();
  const [activeSlide, setActiveSlide] = useState(0);
  
  const plans = [
    {
      title: 'free.title',
      price: 'free.price',
      domain: 'free.domain',
      pageViews: 'free.pageViews',
      features: ['free.feature1', 'free.feature2', 'free.feature3', 'free.feature4']
    },
    {
      title: 'tier1.title',
      price: 'tier1.price',
      domain: 'tier1.domain',
      pageViews: 'tier1.pageViews',
      features: ['tier1.feature1', 'tier1.feature2', 'tier1.feature3', 'tier1.feature4']
    },
    {
      title: 'tier2.title',
      price: 'tier2.price',
      domain: 'tier2.domain',
      pageViews: 'tier2.pageViews',
      features: ['tier2.feature1', 'tier2.feature2', 'tier2.feature3', 'tier2.feature4']
    },
    {
      title: 'tier3.title',
      price: 'tier3.price',
      domain: 'tier3.domain',
      pageViews: 'tier3.pageViews',
      features: ['tier3.feature1', 'tier3.feature2', 'tier3.feature3', 'tier3.feature4']
    },
    {
      title: 'tier4.title',
      price: 'tier4.price',
      domain: 'tier4.domain',
      pageViews: 'tier4.pageViews',
      features: ['tier4.feature1', 'tier4.feature2', 'tier4.feature3', 'tier4.feature4']
    },
    {
      title: 'tier5.title',
      price: 'tier5.price',
      domain: 'tier5.domain',
      pageViews: 'tier5.pageViews',
      features: ['tier5.feature1', 'tier5.feature2', 'tier5.feature3', 'tier5.feature4']
    }
  ];

  const nextSlide = () => setActiveSlide((prev) => (prev === plans.length - 1 ? 0 : prev + 1));
  const prevSlide = () => setActiveSlide((prev) => (prev === 0 ? plans.length - 1 : prev - 1));
  const nextThreeSlides = () => setActiveSlide((prev) => (prev + 3 >= plans.length ? 0 : prev + 3));
  const prevThreeSlides = () => setActiveSlide((prev) => (prev - 3 < 0 ? Math.max(0, plans.length - 3) : prev - 3));

  const visiblePlans = () => {
    const maxVisibleDesktop = 3;
    let visibleItems = [];
    for (let i = 0; i < maxVisibleDesktop; i++) {
      const index = (activeSlide + i) % plans.length;
      visibleItems.push(plans[index]);
    }
    return visibleItems;
  };

  const getCardClass = (index) => {
    return index === 1 
      ? "bg-white rounded-2xl shadow-lg p-8 border-2 border-blue-100 relative transform md:scale-105 z-10" 
      : "bg-white rounded-2xl shadow-md p-8 hover:shadow-lg transition-all duration-300";
  };
  
  const steamVariants = {
    initial: { opacity: 0, y: 0 },
    animate: (i) => ({
      opacity: [0, 1, 0],
      y: -15,
      x: i % 2 === 0 ? 5 : -5,
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "loop",
        delay: i * 0.3
      }
    })
  };

  return (
    // WE VOEGEN HIER HET ID "pricing" TOE VOOR DE SCROLL TRACKING
    <section id="pricing" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center mb-4">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mr-3">
              {t('pricing.coffeeBanner')}
            </h2>
            <div className="relative inline-block">
              <SafeIcon icon={FiCoffee} className="text-3xl lg:text-4xl text-amber-700" />
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    custom={i}
                    variants={steamVariants}
                    initial="initial"
                    animate="animate"
                    className="absolute w-1 h-2 bg-gray-200 rounded-full"
                    style={{ left: `${i * 4 - 4}px` }}
                  />
                ))}
              </div>
            </div>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </motion.div>

        {/* Mobile View */}
        <div className="md:hidden relative">
          <motion.div
            key={activeSlide}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-lg p-8 mx-auto max-w-sm"
          >
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t(`pricing.${plans[activeSlide].title}`)}</h3>
              <p className="text-gray-600 mb-2">{t(`pricing.${plans[activeSlide].domain}`)}</p>
              <div className="bg-blue-100 text-blue-700 rounded-lg py-2 px-4 font-medium mb-6">
                {t(`pricing.${plans[activeSlide].pageViews}`)}
              </div>
              <div className="text-blue-600 font-semibold mb-6">{t('pricing.allFeatures')}</div>
            </div>
            
            <ul className="space-y-4 mb-4">
              {plans[activeSlide].features.map((feature, idx) => (
                <li key={idx} className="flex items-start">
                  <SafeIcon icon={FiCheck} className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">{t(`pricing.${feature}`)}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <div className="flex justify-between mt-6">
            <button onClick={prevSlide} className="bg-white p-3 rounded-full shadow-md hover:bg-gray-100" aria-label="Previous plan">
              <SafeIcon icon={FiArrowLeft} className="text-gray-700" />
            </button>
            <div className="flex space-x-2">
              {plans.map((_, idx) => (
                <button key={idx} onClick={() => setActiveSlide(idx)} className={`w-2 h-2 rounded-full ${activeSlide === idx ? 'bg-blue-500' : 'bg-gray-300'}`} aria-label={`Go to plan ${idx + 1}`} />
              ))}
            </div>
            <button onClick={nextSlide} className="bg-white p-3 rounded-full shadow-md hover:bg-gray-100" aria-label="Next plan">
              <SafeIcon icon={FiArrowRight} className="text-gray-700" />
            </button>
          </div>
        </div>

        {/* Desktop View */}
        <div className="hidden md:block">
          <div className="grid grid-cols-3 gap-6 mb-8">
            {visiblePlans().map((plan, index) => (
              <motion.div
                key={`${activeSlide}-${index}`}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={getCardClass(index)}
              >
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t(`pricing.${plan.title}`)}</h3>
                  <p className="text-gray-600 mb-2">{t(`pricing.${plan.domain}`)}</p>
                  <div className="bg-blue-100 text-blue-700 rounded-lg py-2 px-4 font-medium mb-6">
                    {t(`pricing.${plan.pageViews}`)}
                  </div>
                  <div className="text-blue-600 font-semibold mb-6">{t('pricing.allFeatures')}</div>
                </div>
                
                <ul className="space-y-4 mb-4">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <SafeIcon icon={FiCheck} className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{t(`pricing.${feature}`)}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="flex justify-center space-x-4">
            <button onClick={prevThreeSlides} className="bg-white p-3 rounded-full shadow-md hover:bg-gray-100" aria-label="Previous three plans">
              <SafeIcon icon={FiArrowLeft} className="text-gray-700" />
            </button>
            <div className="flex space-x-2 items-center">
              {[0, 3].map((startIndex) => (
                <button key={startIndex} onClick={() => setActiveSlide(startIndex)} className={`w-2 h-2 rounded-full ${activeSlide === startIndex ? 'bg-blue-500' : 'bg-gray-300'}`} aria-label={`Go to plans starting with plan ${startIndex + 1}`} />
              ))}
            </div>
            <button onClick={nextThreeSlides} className="bg-white p-3 rounded-full shadow-md hover:bg-gray-100" aria-label="Next three plans">
              <SafeIcon icon={FiArrowRight} className="text-gray-700" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustedBy;